// トライアル帯 UI：固定バナー・詳細モーダル
import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation, Trans } from 'react-i18next';
import { useUser } from '@/hooks/UserContext';
import {
    getSignupTrialCalendarDaysRemaining,
    shouldShowSignupTrialBanner,
} from '@/utils/signupTrialDays';
import Modal from '@/components/Modal';
import { Button } from '@/components/Button';
import { SparkleIcon } from '@/components/icons/SparkleIcon';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { buildPlansUrlWithTracking, PLANS_ENTRY_REF_SIGNUP_TRIAL_MODAL } from '@/utils/openPlansWithTracking';

/** fixed バナーぶん、下の本文が隠れないよう確保する高さ */
const BANNER_SPACER_CLASS = 'min-h-14 h-16 shrink-0';

type SignupTrialBannerProps = {
    /** 右のメッセージパネル表示時はメイン列と同幅に揃える */
    isMessageSidebarOpen: boolean;
};

/**
 * サインアップ無料トライアル中の帯。メインコンテンツ列の上部に固定表示する。
 */
const SignupTrialBanner: React.FC<SignupTrialBannerProps> = ({ isMessageSidebarOpen }) => {
    const { t } = useTranslation('signupTrial');
    const { userInfo, isLoading } = useUser();
    // トライアル説明モーダルの開閉
    const [showTrialModal, setShowTrialModal] = useState(false);

    // ユーザーのタイムゾーン基準の「残りカレンダー日数」
    const signupTrialDaysLeft = useMemo(() => {
        if (!userInfo?.signupTrialActive || !userInfo.signupTrialEndsAt) {
            return null;
        }
        return getSignupTrialCalendarDaysRemaining(userInfo.signupTrialEndsAt, userInfo.timeZone);
    }, [userInfo?.signupTrialActive, userInfo?.signupTrialEndsAt, userInfo?.timeZone]);

    // トライアル対象外・読み込み中は何も出さない
    if (isLoading || !shouldShowSignupTrialBanner(userInfo) || signupTrialDaysLeft === null) {
        return null;
    }

    return (
        <>
            {/* ビュー上端に固定。左は PC 時サイドバー分ずらし、右はメッセージパネル幅に追従 */}
            <div
                id="signup-trial-banner"
                role="banner"
                className={clsx(
                    'fixed top-0 z-40 border-b border-white/10 shadow-md transition-[right] duration-300',
                    'left-0 right-0 lg:left-48',
                    isMessageSidebarOpen ? 'lg:right-[400px]' : 'lg:right-0',
                )}
            >
                {/* 帯全体がクリックでモーダル。右端 CTA 用に右パディングを多めに取る */}
                <button
                    type="button"
                    onClick={() => setShowTrialModal(true)}
                    aria-haspopup="dialog"
                    aria-expanded={showTrialModal}
                    className={clsx(
                        'group relative w-full cursor-pointer px-4 py-3 sm:pe-32',
                        'bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900',
                        'opacity-100 transition-opacity duration-200',
                        'hover:opacity-90 active:opacity-[0.85]',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-kotomilThemaColor/50',
                    )}
                >
                    {/* コンテンツ全体（アイコン＋見出し、残り日数＋補足） */}
                    <div
                        className={clsx(
                            'mx-auto flex w-full max-w-full flex-col items-center gap-1.5 sm:flex-row sm:items-center sm:justify-center sm:gap-x-5 lg:gap-x-6',
                            'text-center',
                        )}
                    >
                        {/* 上段（モバイル）：キラアイコン + 「トライアル中」 + 残り日数ピル */}
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="flex items-center gap-1.5 sm:gap-2">
                                <SparkleIcon className="size-3.5 shrink-0 sm:size-4 text-white" />
                                <span className="text-[11px] font-bold text-white tracking-widest sm:text-[13px] lg:text-sm sm:tracking-[0.1em]">
                                    {t('inProgress')}
                                </span>
                            </div>
                            <span className="shrink-0 bg-kotomilThemaColor text-neutral-900 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full shadow-sm sm:text-[12px] lg:text-[13px] sm:px-2.5">
                                <Trans
                                    t={t}
                                    i18nKey="daysLeft"
                                    count={signupTrialDaysLeft}
                                    values={{ count: signupTrialDaysLeft }}
                                    components={{ strong: <strong className="font-black text-[12px] sm:text-[14px] lg:text-[15px] mx-0.5" /> }}
                                />
                            </span>
                        </div>

                        {/* 下段（モバイル）：補足テキスト */}
                        <span className="text-[10px] font-medium leading-snug text-white/80 tracking-wide sm:text-[12px] lg:text-[13px] sm:tracking-[0.08em]">
                            {t('bannerAllAnalysisNote')}
                        </span>
                    </div>

                    {/* 右端固定：詳細モーダルへ誘導（見た目でタップ可能と分かるピル） */}
                    <div
                        className={clsx(
                            // 英語など文字が長いときモバイルで折り返し崩れを防ぐため、sm 以上のみ表示
                            'absolute end-2 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded-full border border-white/30 bg-transparent sm:flex',
                            'px-2 py-1 sm:end-3 sm:px-2.5 sm:py-1.5',
                            'text-[10px] text-white tracking-[0.1em] sm:text-xs sm:tracking-[0.12em]',
                            'transition group-hover:border-white/60 group-hover:bg-white/5',
                        )}
                    >
                        <span>{t('bannerLearnMore')}</span>
                        <ChevronRight
                            className="size-3.5 shrink-0 opacity-90 transition group-hover:translate-x-0.5"
                            strokeWidth={2.25}
                            aria-hidden
                        />
                    </div>
                </button>
            </div>
            {/* fixed バナーと本文の重なり防止 */}
            <div className={BANNER_SPACER_CLASS} aria-hidden />

            {/* トライアル内容の説明（HTML 含む文言は i18n 側で管理） */}
            <Modal
                isOpen={showTrialModal}
                onClose={() => setShowTrialModal(false)}
                contentClassName="w-full max-w-md p-6 sm:p-8"
            >
                {/* モーダル見出し */}
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 pr-6">
                    {t('modalTitle')}
                </h2>
                {/* 説明文（JSON 側の ul/li 等に子セレクタでリストデザインを適用） */}
                <div
                    className={clsx(
                        'mb-4 text-sm leading-relaxed text-gray-800',
                        '[&_p]:mb-0',
                        '[&_ul]:my-3 [&_ul]:list-none [&_ul]:space-y-2 [&_ul]:rounded-lg [&_ul]:border [&_ul]:border-gray-200 [&_ul]:bg-gray-50/80 [&_ul]:px-4 [&_ul]:py-3',
                        '[&_li]:relative [&_li]:pl-6 [&_li]:text-gray-800',
                        "[&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:top-[0.55em] [&_li]:before:h-1.5 [&_li]:before:w-1.5 [&_li]:before:rounded-full [&_li]:before:bg-kotomilThemaColor [&_li]:before:content-['']",
                    )}
                    dangerouslySetInnerHTML={{ __html: t('modalBody') }}
                />
                <div className="mb-6">
                    <Link
                        to={buildPlansUrlWithTracking({
                            source: 'signup_trial_modal',
                            plansEntryRef: PLANS_ENTRY_REF_SIGNUP_TRIAL_MODAL,
                        })}
                        className={clsx(
                            'flex w-full items-center justify-center gap-2 rounded-xl border-2 border-neutral-900',
                            'bg-kotomilThemaColor px-4 py-3.5 text-sm font-bold text-neutral-900 shadow-md',
                            'transition hover:brightness-95 active:brightness-90',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2',
                        )}
                        onClick={() => setShowTrialModal(false)}
                    >
                        {t('modalPlanLink')}
                        <ChevronRight className="size-5 shrink-0" strokeWidth={2.5} aria-hidden />
                    </Link>
                </div>
                {/* 閉じる */}
                <div className="flex justify-center">
                    <Button
                        variant="outline"
                        className="px-8 py-2 border-2 border-gray-900 rounded-lg min-w-[120px]"
                        onClick={() => setShowTrialModal(false)}
                    >
                        {t('modalClose')}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default SignupTrialBanner;
